import { IconDownload, IconLoader2 } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import formatDate from '@/utils/dateFormat';
import truncateText from '@/utils/truncateText';
import calculateConversationDuration from '@/utils/calculateConversationDuration';
import Markdown from '@/components/markdown/Markdown';
import useTranslation from '@/hooks/useTranslation';
import { AllTranscriptsProps } from '@/types';
import MetadataItem from './MetadataItem';
import { useMutateDownloadConversationMedia } from '@/reactQuery/get';
import { Button } from '@/components/ui/button';

interface ConversationMetadataProps {
  conversation?: AllTranscriptsProps | null;
  totalMessages?: number;
  isExpanded?: boolean;
  onSummaryToggle?: (expanded: boolean) => void;
  maxSummaryWords?: number;
  className?: string;
}

export default function ConversationMetadata({
  conversation,
  totalMessages,
  isExpanded = false,
  onSummaryToggle,
  maxSummaryWords = 80,
  className = '',
}: ConversationMetadataProps) {
  const { lang } = useTranslation();
  const { mutateAsync: downloadMedia, isPending: isDownloading } =
    useMutateDownloadConversationMedia();

  const summary = conversation?.digest?.summary ?? '';

  const handleDownloadMedia = async () => {
    if (!conversation?.project_id || !conversation?._id) return;

    try {
      // Generate filename: sanitized project title would be better, but we use ID for simplicity
      const filename = `conversation_${conversation._id}.zip`;
      await downloadMedia({
        projectId: conversation.project_id,
        conversationId: conversation._id,
        filename,
      });
      toast.success(lang.get('msg.downloadStarted') || 'Download started');
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      if (err?.response?.status === 404) {
        toast.error(lang.get('msg.noMediaFound') || 'No media files found for this conversation');
      } else {
        toast.error(lang.get('msg.downloadFailed') || 'Failed to download media');
      }
    }
  };

  // Check if conversation has any user media files
  const hasMediaFiles =
    conversation?.messages?.some(
      (msg) =>
        msg.role === 'user' &&
        msg.data?.some((d: { file_id?: string }) => d.file_id && !d.file_id.includes('/projects/')),
    ) ?? false;

  return (
    <div
      className={`w-[500px] text-dark-text max-h-min md800:w-full overflow-y-auto bg-secondary-background shadow-sm border border-dark-text/10 rounded-lg p-4 ${className}`}
    >
      <div className="flex flex-col gap-3">
        <MetadataItem label={lang.get('msg.conversationID')}>
          {conversation?.conversation_id ?? conversation?._id}
        </MetadataItem>

        <MetadataItem label={lang.get('msg.createdAt')} condition={!!conversation?.createdAt}>
          {formatDate(conversation?.createdAt as string, 'DD/MMM/YYYY - hh:mm:ss A')}
        </MetadataItem>

        <MetadataItem label={lang.get('msg.status')} condition={!!conversation?.status}>
          <span className="capitalize">{conversation?.status}</span>
        </MetadataItem>

        <MetadataItem
          label="Completion time"
          condition={!!(conversation?.startedAt && conversation?.finishedAt)}
        >
          {calculateConversationDuration({
            startedAt: conversation?.startedAt as string,
            finishedAt: conversation?.finishedAt as string,
          })}
        </MetadataItem>

        <MetadataItem
          label={lang.get('msg.metadata')}
          condition={!!(conversation?._id && conversation?.metadata)}
        >
          <div className="capitalize">
            {Object?.entries(conversation?.metadata ?? {})?.map(([key, value]) => (
              <div className="flex font-medium whitespace-nowrap" key={key}>
                <p className="capitalize">{key}</p>:<p className="ml-1">{String(value)}</p>
              </div>
            ))}
          </div>
        </MetadataItem>

        <MetadataItem label={lang.get('msg.totalMessages')}>
          <span className="capitalize">{totalMessages}</span>
        </MetadataItem>

        <MetadataItem label="Conversation summary" condition={!!summary}>
          <div>
            <Markdown>
              {isExpanded ? summary : truncateText(summary as string, maxSummaryWords)}
            </Markdown>
            {(summary ?? '').split(' ').length > maxSummaryWords && onSummaryToggle && (
              <button
                type="button"
                className="ml-1 text-blue-500 hover:underline"
                onClick={() => onSummaryToggle(!isExpanded)}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </MetadataItem>

        {hasMediaFiles && (
          <div className="mt-4 pt-4 border-t border-dark-text/10">
            <Button
              variant="outline"
              onClick={handleDownloadMedia}
              disabled={isDownloading}
              className="w-full flex items-center justify-center gap-2"
            >
              {isDownloading ? (
                <IconLoader2 size={18} className="animate-spin" />
              ) : (
                <IconDownload size={18} />
              )}
              {isDownloading
                ? lang.get('msg.downloading') || 'Downloading...'
                : lang.get('msg.downloadMedia') || 'Download Media'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
